import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, AlertCircle, CheckCircle, Car, RefreshCw } from 'lucide-react';
import jsQR from 'jsqr';

const QRScannerModal = ({ isOpen, onClose, onScanSuccess, title = "QR Kod Tara" }) => {
  // Mobil cihaz tespiti - mobilde arka, masaüstünde ön kamera
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Kullanıcının son seçtiği kamera tercihini localStorage'dan oku
  const getSavedFacingMode = () => {
    const saved = localStorage.getItem('veribil_camera_facing_mode');
    if (saved && (saved === 'user' || saved === 'environment')) {
      console.log('Kaydedilmiş kamera tercihi bulundu:', saved);
      return saved;
    }
    // Kayıtlı tercih yoksa default: mobilde arka, masaüstünde ön
    const defaultMode = isMobile ? 'environment' : 'user';
    console.log('Default kamera modu:', defaultMode);
    return defaultMode;
  };

  const [hasPermission, setHasPermission] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [facingMode, setFacingMode] = useState(getSavedFacingMode()); // Son tercih veya default
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      requestCameraPermission();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [isOpen]);

  // Orientation change ve window resize event'lerini handle et
  useEffect(() => {
    const handleOrientationChange = () => {
      // Kamera çalışıyorsa ve video element varsa, kısa bir gecikme sonra video'yu yenile
      if (hasPermission && streamRef.current && videoRef.current) {
        console.log('Cihaz yönü değişti, video yeniden başlatılıyor...');
        setTimeout(() => {
          if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play().catch(err => {
              console.error('Video yeniden başlatma hatası:', err);
            });
          }
        }, 300);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [hasPermission]);

  // HasPermission değiştiğinde video setup'ını yap
  useEffect(() => {
    if (hasPermission && streamRef.current && videoRef.current) {
      console.log('Setting up video with existing stream...');
      setupVideo();
    }
  }, [hasPermission]);

  const setupVideo = () => {
    if (!videoRef.current || !streamRef.current) {
      console.error('Video ref or stream not available for setup');
      return;
    }

    console.log('Video ref found, assigning stream...');
    videoRef.current.srcObject = streamRef.current;
    console.log('Stream assigned to video element');

    // Video loading event'lerini handle et
    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded, dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
      if (videoRef.current) {
        videoRef.current.play().then(() => {
          console.log('▶️ Video playing');
          // Video oynatılmaya başladığında kısa bir gecikme sonrası taramayı başlat
          setTimeout(() => {
            startScanning();
          }, 500);
        }).catch(playError => {
          console.error('Video play error:', playError);
          setError('Video oynatma hatası. Manual giriş kullanın.');
          setShowManualInput(true);
        });
      }
    };

    const handleLoadedData = () => {
      console.log('Video data loaded');
    };

    const handleCanPlay = () => {
      console.log('Video canplay event');
    };

    // Event listener'ları ekle
    videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoRef.current.addEventListener('loadeddata', handleLoadedData);
    videoRef.current.addEventListener('canplay', handleCanPlay);

    console.log('Video event listeners added');

    // Manual play attempt
    setTimeout(() => {
      if (videoRef.current && videoRef.current.srcObject) {
        console.log('Manual video play attempt...');
        videoRef.current.play().catch(err => {
          console.error('Manual play failed:', err);
        });
      }
    }, 100);
  };

  const requestCameraPermission = async (preferredFacingMode = facingMode) => {
    try {
      setError('');
      console.log('Kamera izni isteniyor, facing mode:', preferredFacingMode);

      // Eski stream varsa durdur
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      let stream;

      // Öncelik sırasına göre kamera erişimi
      try {
        console.log(`${preferredFacingMode} kamera erişimi deneniyor...`);
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: preferredFacingMode },
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        });
        console.log(`${preferredFacingMode} kamera stream alındı:`, stream);
      } catch (primaryError) {
        console.warn(`${preferredFacingMode} kamera başarısız, alternatif deneniyor:`, primaryError);
        const alternateFacingMode = preferredFacingMode === 'environment' ? 'user' : 'environment';
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: alternateFacingMode },
              width: { ideal: 1280, min: 640 },
              height: { ideal: 720, min: 480 }
            }
          });
          console.log(`${alternateFacingMode} kamera stream alındı:`, stream);
          setFacingMode(alternateFacingMode); // Başarılı olan modu set et
        } catch (alternateError) {
          console.warn('Alternatif kamera da başarısız, herhangi bir kamera deneniyor:', alternateError);
          // Son seçenek - herhangi bir kamera
          stream = await navigator.mediaDevices.getUserMedia({
            video: true
          });
          console.log('Genel kamera stream alındı:', stream);
        }
      }

      streamRef.current = stream;
      console.log('Stream saved to ref');
      setHasPermission(true);
      console.log('HasPermission set to true');
    } catch (err) {
      console.error('Camera permission error:', err);
      let errorMessage = 'Kamera erişimi reddedildi veya kullanılamıyor.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Kamera izni reddedildi. Tarayıcı ayarlarından kamera erişimine izin verin.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Kamera bulunamadı. Cihazınızda kamera olduğundan emin olun.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Kamera kullanımda veya erişilemiyor. Diğer uygulamaları kapatıp tekrar deneyin.';
      }

      setError(errorMessage + ' Manuel kod girişi kullanın.');
      setShowManualInput(true);
    }
  };

  const startScanning = () => {
    setScanning(true);
    scanIntervalRef.current = setInterval(() => {
      scanQRCode();
    }, 300); // Her 300ms'de bir tara (daha hızlı)
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      try {
        // jsQR ile gerçek QR kod tarama
        const qrResult = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });

        if (qrResult && qrResult.data) {
          handleQRResult(qrResult);
        }
      } catch (err) {
        console.error('QR scanning error:', err);
      }
    }
  };

  const handleQRResult = (result) => {
    if (result && result.data) {
      const qrData = result.data.trim();

      // Taranan QR kodunu console'da göster
      console.log('QR Kod Tarandı:', qrData);

      // Sadece "veribil_" ile başlayan QR kodları kabul et
      if (qrData.toLowerCase().startsWith('veribil_')) {
        stopScanning();
        stopCamera(); // Kamerayı kapat
        // "veribil_" prefix'ini kaldırarak sadece ID'yi gönder
        const vehicleId = qrData.substring(8); // "veribil_" 8 karakterdir
        console.log('Geçerli Veribil QR:', qrData, '-> Araç ID:', vehicleId);
        console.log('Kamera kapatıldı, teslim ekranına geçiliyor...');
        onScanSuccess(vehicleId);
        // handleClose(); // QR modal'ı kapatma - parent (DeliveryDialog) kendisi kapatacak
      } else {
        // Başka kod tarandığında console'a yaz ama taramaya devam et
        console.log('️ Başka kod taratıldı:', qrData, '- Veribil QR bekleniyor...');
        // QR kod geçersizse taramaya devam et, modalı kapatma
      }
    }
  };

  const stopScanning = () => {
    setScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const stopCamera = () => {
    stopScanning();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    // Video element'i de temizle
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setHasPermission(false);
  };

  const switchCamera = async () => {
    console.log('Kamera değiştiriliyor, mevcut:', facingMode);

    // Loading state'i aktif et
    setIsSwitchingCamera(true);

    // Yeni facing mode'u belirle
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    console.log('Yeni kamera modu:', newFacingMode);

    // Kullanıcının tercihini localStorage'a kaydet
    localStorage.setItem('veribil_camera_facing_mode', newFacingMode);
    console.log('Kamera tercihi kaydedildi:', newFacingMode);

    // Eski stream'i durdur
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Taramayı durdur
    stopScanning();

    // State'i güncelle
    setFacingMode(newFacingMode);

    // Kısa gecikme sonrası yeni stream'i al
    setTimeout(async () => {
      try {
        console.log('Kamera izni isteniyor, facing mode:', newFacingMode);

        // Yeni stream al
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: newFacingMode },
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          }
        });

        console.log('Yeni kamera stream alındı:', stream);

        // Stream'i kaydet
        streamRef.current = stream;

        // Video'ya ata
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          console.log('▶️ Video playing');
        }

        // Taramayı yeniden başlat
        setTimeout(() => {
          startScanning();
          setIsSwitchingCamera(false);
        }, 500);

      } catch (err) {
        console.error('Kamera değiştirme hatası:', err);
        setIsSwitchingCamera(false);
      }
    }, 300);
  };

  const handleClose = () => {
    stopCamera();
    setError('');
    setManualCode('');
    setShowManualInput(false);
    onClose();
  };

  const handleManualSubmit = () => {
    if (manualCode.trim()) {
      onScanSuccess(manualCode.trim());
      // handleClose(); // QR modal'ı kapatma - parent (DeliveryDialog) kendisi kapatacak
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {!showManualInput && (
          <div className="mb-4">
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: '256px', height: '256px' }}>
              <video
                ref={videoRef}
                width="640"
                height="480"
                autoPlay
                muted
                playsInline
                className="absolute top-0 left-0 w-full h-full object-cover bg-black z-10"
                style={{
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
              />
              <canvas ref={canvasRef} className="hidden" />

              {/* Kamera Değiştirme Loading Overlay */}
              {isSwitchingCamera && (
                <div className="absolute inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
                  <div className="text-center">
                    <RefreshCw className="w-12 h-12 text-white animate-spin mx-auto mb-3" />
                    <p className="text-white text-sm font-medium">Kamera değiştiriliyor...</p>
                  </div>
                </div>
              )}

              {/* Tarama çerçevesi - sadece hasPermission true ise */}
              {hasPermission && (
                <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                  <div className="w-48 h-48 border-2 border-white border-dashed rounded-lg animate-pulse"></div>
                </div>
              )}

              {/* Tarama çizgisi animasyonu */}
              {scanning && hasPermission && (
                <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
                  <div className="w-48 h-48 relative">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-red-500 animate-bounce"></div>
                  </div>
                </div>
              )}

              {/* Durum göstergesi */}
              {scanning && hasPermission && (
                <div className="absolute inset-x-0 top-4 flex justify-center z-30 pointer-events-none">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm flex items-center">
                    <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                    Müşteri QR kodunu taratınız
                  </div>
                </div>
              )}

              {/* Kamera Değiştirme Butonu - sadece hasPermission true ise */}
              {hasPermission && (
                <div className="absolute inset-x-0 bottom-4 flex justify-center z-30">
                  <button
                    onClick={switchCamera}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium flex items-center shadow-lg transition-all pointer-events-auto"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Kamera Değiştir
                  </button>
                </div>
              )}

              {/* Köşe işaretleri - sadece hasPermission true ise */}
              {hasPermission && (
                <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                  <div className="w-48 h-48 relative">
                    {/* Sol üst */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-l-4 border-t-4 border-green-500"></div>
                    {/* Sağ üst */}
                    <div className="absolute top-0 right-0 w-6 h-6 border-r-4 border-t-4 border-green-500"></div>
                    {/* Sol alt */}
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-l-4 border-b-4 border-green-500"></div>
                    {/* Sağ alt */}
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-r-4 border-b-4 border-green-500"></div>
                  </div>
                </div>
              )}
            </div>

            {hasPermission && (
              <>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Müşteri QR kodunu tarama alanına getirin
                </p>
                <p className="text-xs text-gray-500 text-center">
                  Diğer QR kodlar otomatik olarak göz ardı edilir
                </p>
              </>
            )}
          </div>
        )}

        {showManualInput && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manuel Kod Girişi
            </label>
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Referans No (1, 2, 3...), Transaction ID veya Plaka"
              className="form-input w-full"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              İpucu: Referans no için sadece sayıyı girin (örn: 1). Lokasyon öneki otomatik eklenecek.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {!showManualInput && hasPermission && !isSwitchingCamera && (
            <button
              onClick={() => setShowManualInput(true)}
              className="btn-secondary flex items-center justify-center"
            >
              <Car className="w-4 h-4 mr-2" />
              Manuel Giriş
            </button>
          )}

          {showManualInput && (
            <button
              onClick={handleManualSubmit}
              disabled={!manualCode.trim()}
              className="btn-primary flex items-center justify-center disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Teslim Et
            </button>
          )}

          {!hasPermission && !showManualInput && !isSwitchingCamera && (
            <button
              onClick={requestCameraPermission}
              className="btn-primary flex items-center justify-center"
            >
              <Camera className="w-4 h-4 mr-2" />
              Kamera İzni Ver
            </button>
          )}

          {!isSwitchingCamera && (
            <button
              onClick={handleClose}
              className="btn-secondary"
            >
              İptal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;